import { provide, ref, watch, type Ref } from 'vue';

import { Constants } from '@/constants';
import { currentLocale, i18n } from '@/plugins/i18n';
import type { LanguageEnabled, Locales } from '@/services/types/api.types';
import { useCommunityStore } from '@/stores';

export function formatMoney(value: number | undefined): string {
  if (value === undefined) return '';
  return Intl.NumberFormat(`${currentLocale()}-CA`, {
    currency: 'CAD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(value);
}

export function getLanguageEnabled(obj?: LanguageEnabled | null): Locales[] {
  const community = useCommunityStore().community;
  return obj?.language_enabled ?? community?.language_enabled ?? [Constants.DefaultLocale];
}

export function getTranslation(field: any, key: string, languageEnabled?: Locales[]): string {
  const locale = currentLocale();
  if (languageEnabled?.includes(locale) && field?.[key]?.[locale]) {
    return field[key][locale];
  }
  const community = useCommunityStore().community;
  const locales =
    languageEnabled
      ?.filter((x) => x !== locale)
      ?.sort((x, y) => {
        if (x === community?.default_language) return -2;
        if (x === Constants.DefaultLocale) return -1;
        return x > y ? 0 : 1;
      }) ?? [];
  for (const l of locales) {
    if (field?.[key]?.[l]) return `${field[key][l]} (${l})`;
  }
  return '';
}

export function provideEditLocale(languageEnabled: Ref<Locales[]>): Ref<Locales> {
  const locale = currentLocale();
  const editLocale = ref<Locales>(locale);
  const community = useCommunityStore().community;
  languageEnabled.value ??= community?.language_enabled ?? [Constants.DefaultLocale];
  const selectEditLocale = (locale: Locales, languageEnabled: Locales[] = []): Locales => {
    const defaultLocale =
      community && languageEnabled.includes(community.default_language)
        ? community.default_language
        : languageEnabled?.[0];
    const selectedLocale = languageEnabled.includes(locale) ? locale : defaultLocale;
    return selectedLocale ?? Constants.DefaultLocale;
  };
  watch(languageEnabled, (newValue) => {
    editLocale.value = selectEditLocale(editLocale.value, newValue);
  });
  editLocale.value = selectEditLocale(locale, languageEnabled.value);
  provide('editLocale', editLocale);
  return editLocale;
}

type DisplayedModule = { title: any; block_type?: number; parent_type?: number; config?: any };

// A project's Hero can show its project's title instead of its own. A page lends its Hero no title, so the web draws
// the Hero's own.
const showsProjectTitle = (module: DisplayedModule): boolean =>
  module.block_type === Constants.Block.Type.Hero &&
  !!module.config?.useProjectTitle &&
  module.parent_type !== Constants.Block.ParentType.Page;

/**
 * Get the display title for a module, respecting the useProjectTitle flag for Hero modules.
 * Returns the project title when useProjectTitle is true for a Hero module of a project, which is empty until the
 * project is loaded, otherwise returns the module title.
 */
export function getModuleDisplayTitle(
  module: DisplayedModule | undefined | null,
  project: { title: any } | undefined | null,
  languageEnabled?: Locales[],
): string {
  if (!module) return '';
  if (showsProjectTitle(module)) return getTranslation(project, 'title', languageEnabled);
  return getTranslation(module, 'title', languageEnabled);
}

/**
 * The title a block is listed and headed by, which is never empty once the block is loaded. A Hero on its project's
 * title says so while that title is not there to show; any other block without a title is named by its type.
 */
export function getModuleDisplayTitleWithFallback(
  module: DisplayedModule | undefined | null,
  project: { title: any } | undefined | null,
  languageEnabled: Locales[] | undefined,
  typeName: string,
): string {
  const title = getModuleDisplayTitle(module, project, languageEnabled);
  if (title || !module) return title;
  return showsProjectTitle(module) ? i18n.global.t('project-module.hero.usingProjectTitle') : typeName;
}
